import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import { ProductsContainer, BreadcrumbsWrapper }
  from '@fuww/library/src/CompanyProfile';
import { Breadcrumbs } from '@fuww/library/src/Breadcrumb';
import Head from '../Head';
import withQuery from '../../lib/withQuery';
import profileBySlugQuery
  from '../../lib/queries/products/profileBySlugQuery.graphql';
import CompanyProfileProducts
  from './CompanyProfilePages/Products';
import DynamicProducts from '../Products/DynamicProducts';
import messages from '../../lib/messages.json';
import HomeLink from '../HomeLink';
import LinkAndAnchor from '../LinkAndAnchor';
import {
  companyProfileCollectionImageConfigurations,
  companyProfileImageConfigurations,
  companyProfileLogoImageConfigurations,
  companyProfileMobileImageConfigurations,
} from '../../lib/imageConfigurations';

const companyProductsPropertyTypes = {
  data: PropTypes.shape({
    profileBySlug: PropTypes.shape({
      id: PropTypes.string.isRequired,
      brands: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
      }).isRequired).isRequired,
      page: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

type CompanyProductsProperties = InferProps<
  typeof companyProductsPropertyTypes
>;

const CompanyProducts = ({
  data: { profileBySlug },
}: CompanyProductsProperties) => {
  const intl = useIntl();

  const { id: profileId, brands, page: { slug, title } } = profileBySlug;

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="company-directory"
      passHref
    >
      {intl.formatMessage(messages.companies)}
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="company-profile-products"
      passHref
      params={{ slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        title={
          intl.formatMessage(
            messages['company-profile-products.title'], { title },
          )
        }
        description={
          intl.formatMessage(messages['company-profile.description'], { title })
        }
        subjectType="Profile"
        subjectId={profileId}
        payload={{ marketId: 1 }}
      />
      <BreadcrumbsWrapper>
        <Breadcrumbs
          crumbs={breadcrumbs}
        />
      </BreadcrumbsWrapper>
      <CompanyProfileProducts profile={profileBySlug}>
        <ProductsContainer>
          <DynamicProducts
            brandIdsScope={brands.map(({ id }) => id)}
            marketId={1}
            route="company-profile-product"
            includeProfile
          />
        </ProductsContainer>
      </CompanyProfileProducts>
    </>
  );
};

CompanyProducts.propTypes = companyProductsPropertyTypes;

const CompanyProductsWithQuery = withQuery(CompanyProducts);

const companyProductsPagePropertyTypes = {
  slug: PropTypes.string.isRequired,
};

type CompanyProductsPageProperties = InferProps<
  typeof companyProductsPagePropertyTypes
>;

const CompanyProductsPage = ({ slug }: CompanyProductsPageProperties) => (
  <CompanyProductsWithQuery
    query={profileBySlugQuery}
    variables={{
      slug,
      imageConfigurations: companyProfileImageConfigurations,
      mobileImageConfigurations: companyProfileMobileImageConfigurations,
      logoConfigurations: companyProfileLogoImageConfigurations,
      collectionImageConfigurations:
        companyProfileCollectionImageConfigurations,
    }}
    errorMessage="Error loading this company profile"
  />
);

CompanyProductsPage.propTypes = companyProductsPagePropertyTypes;

export { CompanyProducts };
export default CompanyProductsPage;
